import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Badge, Dropdown } from 'react-bootstrap';
import { useAuth } from '@/contexts/AuthContext';
import { ROLE_BADGE_COLORS, ROLE_LABELS } from '@/auth/roles';
import { useUi } from '@/contexts/UiContext';
import { useUnreadCount } from '@/hooks/useNotifications';
import { useFilterBarApplicable } from '@/hooks/useFilterBarApplicable';
import { FeedbackModal } from '@/components/shared/FeedbackModal';
import { BrandMark } from '@/components/shared/BrandMark';
import { GlobalFilterBar } from './GlobalFilterBar';

export function Header() {
  const { userName, userEmail, userPictureUrl, role, logout } = useAuth();
  const { filterBarVisible, filterBarPinned, toggleFilterBar } = useUi();
  const filterBarApplicable = useFilterBarApplicable();
  const { data: unreadCount = 0 } = useUnreadCount();
  const [showFeedback, setShowFeedback] = useState(false);
  const navigate = useNavigate();

  return (
    <header className="app-header bg-body">
      <div className="d-flex align-items-center justify-content-between px-3 py-2 border-bottom">
        <BrandMark />
        <div className="d-flex align-items-center gap-3">
          {filterBarApplicable && (
            <button
              type="button"
              className="btn btn-link text-body p-0"
              onClick={toggleFilterBar}
              aria-expanded={filterBarVisible}
              aria-pressed={filterBarPinned}
              aria-label={filterBarVisible ? 'Hide filters' : 'Show filters'}
              title={filterBarVisible ? 'Hide filters' : 'Show filters'}
            >
              <i
                className={`bi ${filterBarPinned ? 'bi-funnel-fill' : 'bi-funnel'} fs-5`}
                aria-hidden="true"
              />
            </button>
          )}

          <button
            type="button"
            className="btn btn-link text-body position-relative p-0"
            aria-label={`Notifications (${unreadCount} unread)`}
            title="Notifications"
            onClick={() => navigate('/notifications')}
          >
            <i className="bi bi-bell fs-5" aria-hidden="true" />
            {unreadCount > 0 && (
              <Badge
                bg="danger"
                pill
                className="position-absolute top-0 start-100 translate-middle"
                style={{ fontSize: '0.6rem' }}
              >
                {unreadCount > 99 ? '99+' : unreadCount}
              </Badge>
            )}
          </button>

          <Dropdown align="end">
            <Dropdown.Toggle
              variant="link"
              size="sm"
              className="d-flex align-items-center gap-2 dropdown-toggle text-body text-decoration-none border-0 bg-transparent"
            >
              {userPictureUrl ? (
                <img
                  src={userPictureUrl}
                  alt=""
                  width={28}
                  height={28}
                  className="rounded-circle"
                  referrerPolicy="no-referrer"
                />
              ) : (
                <i className="bi bi-person-circle fs-5" aria-hidden="true" />
              )}
              <span className="d-none d-md-inline">{userName ?? 'Account'}</span>
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Header>
                <div className="fw-semibold">{userName}</div>
                <div className="small text-muted">{userEmail}</div>
                <div className="mt-1">
                  {role && <Badge bg={ROLE_BADGE_COLORS[role]}>{ROLE_LABELS[role]}</Badge>}
                </div>
              </Dropdown.Header>
              <Dropdown.Divider />
              <Dropdown.Item as={Link} to="/settings/profile">
                <i className="bi bi-person me-2" aria-hidden="true" />
                Profile
              </Dropdown.Item>
              <Dropdown.Item as={Link} to="/settings">
                <i className="bi bi-sliders me-2" aria-hidden="true" />
                Settings
              </Dropdown.Item>
              <Dropdown.Item onClick={() => setShowFeedback(true)}>
                <i className="bi bi-chat-square-text me-2" aria-hidden="true" />
                Feedback
              </Dropdown.Item>
              <Dropdown.Divider />
              <Dropdown.Item onClick={logout}>
                <i className="bi bi-box-arrow-right me-2" aria-hidden="true" />
                Sign out
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </div>
      </div>

      <GlobalFilterBar />

      {/* Email Explorer renders its own page-local filter bar into this slot
          (via portal) so the bar sits in the sticky header, above the page
          content, exactly like the global bar does on other pages. */}
      <div id="page-filter-bar-portal" />

      <FeedbackModal show={showFeedback} onClose={() => setShowFeedback(false)} />
    </header>
  );
}
